import { useState } from "react";
import { IonItem } from "@ionic/react";
import { Colors, Icon } from "swing-components";

import { translate } from "~utils";

import { EditableLink, EditableMarkdown } from "../EditableFields/";
import { useUpdateSchool } from "./hooks";
import { Footer, ProfileCard } from "./ProfileCard";
import { ErrorList, SchoolProfileSummaryFormSchema } from "./SchoolProfileFormUtils";

type AddressBody = {
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
};

type SchoolProfileSummaryProps = {
  name: string;
  address: AddressBody;
  schoolId: string;
  websiteUrl?: string;
  summary?: string;
};

export const SchoolProfileSummary = ({
  name,
  address,
  websiteUrl: websiteUrl,
  summary,
  schoolId,
}: SchoolProfileSummaryProps) => {
  const [editing, setEditing] = useState(false);

  const [formState, setFormState] = useState({
    websiteUrl: websiteUrl,
    summary: summary,
  });

  const googleMapsLink = `http://www.google.com/maps/search/?api=1&query=
  ${encodeURIComponent(
    `${address.streetAddress} ${address.city}, ${address.state}, ${address.zip}`,
  )}`;

  const { errors, updateSchool } = useUpdateSchool({
    formState,
    validationSchema: SchoolProfileSummaryFormSchema,
    schoolId,
  });

  const onSave = async () => {
    updateSchool()
      .then(() => setEditing(false))
      .catch();
  };

  return (
    <ProfileCard header={name} isEditing={editing} onClick={() => setEditing(true)}>
      <a
        css={{ display: "flex", flexDirection: "column" }}
        className="swing-link"
        target="_blank"
        rel="noreferrer"
        href={googleMapsLink}
      >
        <div className="flex w-full items-center gap-4">
          <Icon name="Location" color={Colors.blue500} />
          {address.streetAddress}
          <br />
          {address.city}, {address.state}, {address.zip}
        </div>
      </a>

      <EditableLink
        lines="none"
        isEditing={editing}
        label={translate("SCHOOL_WEBSITE_LABEL")}
        placeholder={translate("SCHOOL_WEBSITE_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              websiteUrl: e.detail.value,
            };
          });
        }}
        value={formState.websiteUrl}
      />

      <EditableMarkdown
        lines="none"
        isEditing={editing}
        label={translate("SCHOOL_SUMMARY_LABEL")}
        placeholder={translate("SCHOOL_SUMMARY_PLACEHOLDER")}
        onChange={(e) => {
          setFormState((old) => {
            return {
              ...old,
              summary: e.toString(),
            };
          });
        }}
        value={formState.summary}
      />
      {editing && (
        <>
          {errors.length > 0 && (
            <IonItem lines="none">
              <ErrorList errorList={errors} />
            </IonItem>
          )}

          <Footer onSubmit={onSave} onCancel={() => setEditing(false)} />
        </>
      )}
    </ProfileCard>
  );
};
